import {
  ArrowUpRight,
  BookOpenCheck,
  Repeat,
  FileText,
  ClipboardCheck,
  ListChecks,
  Brain,
  Clock,
  Target,
  MessageSquareText,
  Video,
  BellRing,
  LineChart,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { BOOK_TRIAL_URL } from '../lib/config';

const METHOD_STAGES = [
  {
    step: '01',
    title: 'Structured teaching',
    icon: BookOpenCheck,
    body:
      'We follow a carefully organised programme aligned to the AQA and OCR specifications. New content is taught clearly, logically and with a strong focus on what students need for their exams.',
  },
  {
    step: '02',
    title: 'Retrieval practice',
    icon: Repeat,
    body:
      'Professionally designed retrieval questions revisit previous learning regularly, helping students strengthen recall and retain important knowledge over time.',
  },
  {
    step: '03',
    title: 'Exam-question practice',
    icon: FileText,
    body:
      'Students apply what they have learned to exam-style questions, developing the technique needed to turn knowledge into marks.',
  },
  {
    step: '04',
    title: 'Testing & feedback',
    icon: ClipboardCheck,
    body:
      'Regular assessments identify strengths and gaps. Students receive clear teacher feedback so they know exactly what to improve next.',
  },
  {
    step: '05',
    title: 'Accountability & organisation',
    icon: ListChecks,
    body:
      'Regular reminders and check-ins help students keep on top of lessons, homework, revision and assigned practice throughout the course.',
  },
];

const WHY_CARDS = [
  {
    icon: Brain,
    title: 'Retrieval, not just rereading',
    body:
      'Students regularly recall previously learned material rather than simply reviewing it passively.',
  },
  {
    icon: Clock,
    title: 'Practice over time',
    body:
      'Important knowledge is revisited throughout the course instead of being taught once and forgotten.',
  },
  {
    icon: Target,
    title: 'Frequent application',
    body:
      'Exam questions are integrated into teaching so students learn how to use their knowledge under exam conditions.',
  },
  {
    icon: MessageSquareText,
    title: 'Feedback that directs improvement',
    body:
      'Testing and teacher feedback help students understand what they know, what they don’t and what to work on next.',
  },
];

const WEEK_STEPS = [
  { label: 'Live lesson', icon: Video },
  { label: 'Retrieval practice', icon: Repeat },
  { label: 'Homework', icon: BookOpenCheck },
  { label: 'Exam questions', icon: FileText },
  { label: 'Teacher feedback', icon: MessageSquareText },
  { label: 'Progress check', icon: LineChart },
];

const ORG_ITEMS = [
  {
    icon: ListChecks,
    title: 'Clear weekly tasks',
    body: 'Students know what they need to complete between lessons.',
  },
  {
    icon: BellRing,
    title: 'Regular reminders',
    body: 'We help students stay on top of assigned work.',
  },
  {
    icon: LineChart,
    title: 'Progress check-ins',
    body: 'Performance is monitored so gaps can be identified early.',
  },
];

function MethodStage({ stage, index }) {
  const Icon = stage.icon;
  const isLast = index === METHOD_STAGES.length - 1;
  return (
    <li className="relative flex gap-5" data-testid={`method-stage-${stage.step}`}>
      {/* Step marker + rail */}
      <div className="flex flex-col items-center">
        <div className="relative z-10 grid place-items-center w-14 h-14 rounded-2xl bg-brand-sun text-brand-ink shadow-sm shrink-0">
          <Icon className="w-6 h-6" aria-hidden="true" />
        </div>
        {!isLast && (
          <span
            className="w-px flex-1 bg-brand-ink/15 my-2"
            aria-hidden="true"
          />
        )}
      </div>

      <div className={`pb-8 ${isLast ? 'pb-0' : ''}`}>
        <div className="text-brand-coral text-xs font-semibold uppercase tracking-widest">
          Step {stage.step}
        </div>
        <h3 className="mt-1 font-display text-2xl text-brand-ink leading-tight">
          {stage.title}
        </h3>
        <p className="mt-2 text-brand-ink/75 text-base leading-relaxed">
          {stage.body}
        </p>
      </div>
    </li>
  );
}

/**
 * Snake / serpentine diagram of the 5-stage method (desktop only).
 * Row 1 (LTR): 01 → 02 → 03. Curve down at the right edge.
 * Row 2 (RTL): 04 → 05. Numbered yellow circles sit on a curved path.
 */
function MethodSnake() {
  // Compacted layout: rows sit ~158 px apart in viewBox, text blocks sit
  // 40 px away from their nodes so the diagram breathes.
  const NODES = [
    { ...METHOD_STAGES[0], x: 12, y: 35.7, row: 'top' },
    { ...METHOD_STAGES[1], x: 50, y: 35.7, row: 'top' },
    { ...METHOD_STAGES[2], x: 88, y: 35.7, row: 'top' },
    { ...METHOD_STAGES[3], x: 88, y: 63.9, row: 'bottom' },
    { ...METHOD_STAGES[4], x: 12, y: 63.9, row: 'bottom' },
  ];

  return (
    <div
      className="relative w-full"
      style={{ height: 640 }}
      aria-hidden="true"
    >
      {/* Curved connector path - thicker line, smoother curve */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1000 640"
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="snake-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FFC200" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#FFC200" stopOpacity="0.95" />
          </linearGradient>
        </defs>
        {/*
         * Path flow: Row 1 (LTR) -> smooth U-curve down at the right edge ->
         * Row 2 (RTL). One continuous <path> so 03 -> 04 reads as one line.
         */}
        <path
          d="
            M 120 229
            L 880 229
            C 990 229, 990 409, 880 409
            L 120 409
          "
          fill="none"
          stroke="url(#snake-grad)"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {/* Nodes */}
      {NODES.map((n) => {
        const Icon = n.icon;
        const textAbove = n.row === 'top';
        return (
          <div
            key={n.step}
            className="absolute"
            style={{
              left: `${n.x}%`,
              top: `${n.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
            data-testid={`method-node-${n.step}`}
          >
            {/* Text card - consistent width, breathing room from the node */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-60"
              style={
                textAbove
                  ? { bottom: 'calc(100% + 40px)' }
                  : { top: 'calc(100% + 40px)' }
              }
            >
              <div className="text-brand-coral text-[11px] font-semibold uppercase tracking-widest text-center">
                Step {n.step}
              </div>
              <h3 className="mt-0.5 font-display text-xl xl:text-2xl text-brand-ink leading-tight text-center">
                {n.title}
              </h3>
              <p className="mt-1.5 text-brand-ink/75 text-sm leading-snug text-center">
                {n.body}
              </p>
            </div>

            {/* Node circle */}
            <div className="relative z-10 grid place-items-center w-20 h-20 rounded-full bg-brand-sun text-brand-ink shadow-lg ring-8 ring-brand-cream">
              <Icon className="w-8 h-8" aria-hidden="true" />
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-brand-ink text-brand-sun text-[10px] font-semibold shadow-md leading-none">
                {n.step}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WhyCard({ item }) {
  const Icon = item.icon;
  return (
    <div className="rounded-3xl bg-white border border-brand-ink/10 p-6 md:p-7 shadow-sm hover:shadow-lg transition-shadow">
      <div className="w-11 h-11 rounded-2xl bg-brand-mint/70 grid place-items-center">
        <Icon className="w-5 h-5 text-brand-teal" aria-hidden="true" />
      </div>
      <h3 className="mt-4 font-display text-xl md:text-2xl text-brand-ink leading-tight">
        {item.title}
      </h3>
      <p className="mt-2 text-brand-ink/70 text-sm md:text-base leading-relaxed">{item.body}</p>
    </div>
  );
}

function WeekTimeline() {
  return (
    <div className="mt-8 md:mt-10">
      {/* Desktop: horizontal */}
      <ol
        className="hidden md:flex items-stretch justify-between gap-3 relative"
        aria-label="A typical week with Science Sorted"
      >
        {/* Rail behind everything */}
        <span
          className="absolute left-8 right-8 top-8 h-0.5 bg-brand-ink/10"
          aria-hidden="true"
        />
        {WEEK_STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <li
              key={s.label}
              className="relative flex-1 min-w-0 flex flex-col items-center text-center"
              data-testid={`week-step-${i + 1}`}
            >
              <div className="relative z-10 w-16 h-16 rounded-2xl bg-brand-sun grid place-items-center shadow-sm">
                <Icon className="w-6 h-6 text-brand-ink" aria-hidden="true" />
              </div>
              <div className="mt-3 text-xs font-semibold uppercase tracking-widest text-brand-ink/60">
                Step {i + 1}
              </div>
              <div className="mt-1 font-display text-base lg:text-lg text-brand-ink leading-tight">
                {s.label}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Mobile: vertical */}
      <ol className="md:hidden space-y-4" aria-label="A typical week with Science Sorted">
        {WEEK_STEPS.map((s, i) => {
          const Icon = s.icon;
          const isLast = i === WEEK_STEPS.length - 1;
          return (
            <li key={s.label} className="relative flex gap-4">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-2xl bg-brand-sun grid place-items-center shadow-sm shrink-0">
                  <Icon className="w-5 h-5 text-brand-ink" aria-hidden="true" />
                </div>
                {!isLast && <span className="w-px flex-1 bg-brand-ink/15 my-1" aria-hidden="true" />}
              </div>
              <div className="pb-2">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-ink/60">
                  Step {i + 1}
                </div>
                <div className="font-display text-lg text-brand-ink leading-tight">{s.label}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function Services() {
  return (
    <main>
      {/* Hero */}
      <section className="pt-10 md:pt-16 pb-8 md:pb-10 bg-white">
        <div className="container">
          <div className="max-w-4xl">
            <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
              How we teach
            </div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl md:text-6xl text-brand-ink leading-[1.05]">
              Learn it. <span className="italic text-brand-teal">Retrieve it.</span> Apply it.
            </h1>
            <p className="mt-4 text-brand-ink/75 text-base md:text-lg max-w-2xl">
              Evidence-informed science tuition combining structured teaching, retrieval practice,
              exam-question practice and regular feedback.
            </p>
          </div>
        </div>
      </section>

      {/* Section 1 - The Science Sorted method */}
      <section className="py-12 md:py-16 bg-brand-cream" aria-labelledby="method-heading">
        <div className="container">
          <div className="max-w-3xl">
            <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
              The method
            </div>
            <h2
              id="method-heading"
              className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink"
            >
              The <span className="italic text-brand-teal">Science Sorted</span> method.
            </h2>
            <p className="mt-3 text-brand-ink/70 text-base md:text-lg">
              Five stages that turn understanding into marks.
            </p>
          </div>

          {/* Desktop: snake diagram */}
          <div className="hidden lg:block mt-12">
            <MethodSnake />
          </div>

          {/* Mobile / tablet: vertical timeline */}
          <ol className="lg:hidden mt-10 max-w-2xl">
            {METHOD_STAGES.map((s, i) => (
              <MethodStage key={s.step} stage={s} index={i} />
            ))}
          </ol>
        </div>
      </section>

      {/* Section 2 - Why this approach works */}
      <section className="py-12 md:py-16 bg-white" aria-labelledby="why-heading">
        <div className="container">
          <div className="max-w-3xl">
            <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
              Why it works
            </div>
            <h2
              id="why-heading"
              className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink"
            >
              Built on how students actually learn.
            </h2>
          </div>

          <div className="mt-8 md:mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {WHY_CARDS.map((c) => (
              <WhyCard key={c.title} item={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Section 3 - A typical week */}
      <section
        className="py-12 md:py-16 bg-brand-mint/30"
        aria-labelledby="week-heading"
      >
        <div className="container">
          <div className="max-w-3xl">
            <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
              Every week
            </div>
            <h2
              id="week-heading"
              className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink"
            >
              A typical week with <span className="italic text-brand-teal">Science Sorted</span>.
            </h2>
            <p className="mt-3 text-brand-ink/70 text-base md:text-lg">
              What happens beyond the live hour.
            </p>
          </div>

          <WeekTimeline />
        </div>
      </section>

      {/* Section 4 - Organisation is part of the service */}
      <section className="py-12 md:py-16 bg-white" aria-labelledby="org-heading">
        <div className="container">
          <div className="max-w-3xl">
            <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
              Organisation
            </div>
            <h2
              id="org-heading"
              className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink leading-tight"
            >
              We don&rsquo;t just teach the lesson and disappear.
            </h2>
            <p className="mt-3 text-brand-ink/75 text-base md:text-lg max-w-2xl">
              Students receive regular reminders, assigned tasks and check-ins to help them stay
              organised and keep moving through the programme.
            </p>
          </div>

          <div className="mt-8 md:mt-10 grid sm:grid-cols-3 gap-6 md:gap-8">
            {ORG_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex gap-3">
                  <div className="shrink-0 w-11 h-11 rounded-2xl bg-brand-sun/90 grid place-items-center">
                    <Icon className="w-5 h-5 text-brand-ink" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg md:text-xl text-brand-ink leading-tight">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-brand-ink/70 text-sm md:text-base leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Slim CTA */}
      <section className="py-10 md:py-14" aria-labelledby="how-cta-heading">
        <div className="container">
          <div className="rounded-3xl bg-brand-sun px-6 md:px-10 py-6 md:py-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 md:justify-between">
            <div>
              <h3
                id="how-cta-heading"
                className="font-display text-xl md:text-2xl text-brand-ink leading-tight"
              >
                See the method in action.
              </h3>
              <p className="mt-1 text-brand-ink/80 text-sm md:text-base">
                Book a free one-hour trial lesson and experience how we teach.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="bg-brand-ink text-brand-cream hover:bg-brand-teal rounded-full h-12 px-6 shrink-0"
            >
              <a
                href={BOOK_TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2"
                data-testid="how-book-trial-cta"
              >
                Book a free trial <ArrowUpRight className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
